/**
 * apps/command/src/ui — the §15.6 component inventory, P0 subset.
 *
 * EVERY STYLE VALUE COMES FROM THE TAILWIND THEME. No hex, no ad-hoc px,
 * anywhere in this app — the admin analogue of kiosk invariant 7, enforced by
 * `npm run mbt:check`. If a component needs a colour that is not in
 * `tailwind.config.cjs`, the answer is to amend §15.2's table, not to inline a
 * value here.
 *
 * EVERY USER-VISIBLE STRING COMES FROM `t()`. These primitives therefore take
 * ALREADY-TRANSLATED text as props and never contain a literal of their own —
 * which is what keeps the gate's rule simple enough to be mechanical.
 */
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import type { KioskStatus } from '@navoiy/domain';

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------

export function Panel({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    /* `bg-card` is the top-edge light that makes a panel read as raised; the
       lift on hover is 12 px of shadow, not movement — a card that jumps under
       the cursor is a card an operator's eye has to re-find. */
    <section
      className={`animate-settle rounded-card border border-hairline bg-panel bg-card shadow-card transition-shadow duration-state hover:shadow-lift ${className}`}
    >
      {children}
    </section>
  );
}

export function PanelHeader({
  title,
  aside,
}: {
  title: string;
  aside?: ReactNode;
}): React.JSX.Element {
  return (
    <header className="flex items-baseline justify-between gap-4 border-b border-hairline px-4 py-3">
      <h2 className="text-label font-medium text-ink-300">{title}</h2>
      {aside ? <div className="text-micro text-ink-500 tabular-nums">{aside}</div> : null}
    </header>
  );
}

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

/**
 * §15.2 — status colours appear ONLY as status.
 *
 * The map is exhaustive over `KioskStatus` by type, so adding a sixth status to
 * the domain is a compile error here rather than a tile that silently renders
 * in the default colour.
 */
const STATUS_STYLE: Readonly<Record<KioskStatus, string>> = {
  healthy: 'border-ok/40 bg-ok/10 text-ok',
  warning: 'border-warn/40 bg-warn/10 text-warn',
  critical: 'border-crit/40 bg-crit/10 text-crit',
  offline: 'border-offline/40 bg-offline/10 text-offline',
  maintenance: 'border-brand/40 bg-brand/10 text-brand',
};

export function StatusPill({
  status,
  label,
}: {
  status: KioskStatus;
  label: string;
}): React.JSX.Element {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-micro font-medium ${STATUS_STYLE[status]}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
      {label}
    </span>
  );
}

/**
 * §15.5 — the 2 s pulse, and the ONLY animated element in the panel. It marks
 * something that is genuinely live; using it decoratively would destroy the one
 * signal an operator can trust at a glance across a wall display.
 */
export function LiveDot({ label }: { label: string }): React.JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5 text-micro text-ink-500">
      <span className="h-1.5 w-1.5 animate-live rounded-full bg-ok" aria-hidden="true" />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Figures
// ---------------------------------------------------------------------------

/**
 * §15.7's large figure with yesterday's delta, and §15.10's honest empty state.
 *
 * `value` is `string | null`, and `null` renders the caller's `absent` text —
 * «hali yoʻq» for a figure that does not exist yet (attendance until P3), never
 * a zero. A zero here would be a claim, and it would be the most flattering
 * available one.
 */
export function Figure({
  label,
  value,
  absent,
  delta,
  deltaLabel,
  tone = 'neutral',
}: {
  label: string;
  value: string | null;
  absent: string;
  delta?: string | null;
  deltaLabel?: string;
  tone?: 'neutral' | 'ok' | 'warn' | 'crit';
}): React.JSX.Element {
  const toneClass =
    tone === 'ok'
      ? 'text-ok'
      : tone === 'warn'
        ? 'text-warn'
        : tone === 'crit'
          ? 'text-crit'
          : 'text-ink-100';
  return (
    <Panel className="px-5 py-4">
      <p className="text-label text-ink-500">{label}</p>
      {value === null ? (
        <p className="mt-2 text-lead text-ink-500">{absent}</p>
      ) : (
        <p className={`mt-1 text-hero font-semibold tabular-nums ${toneClass}`}>{value}</p>
      )}
      {value !== null && delta ? (
        <p className="mt-1 text-micro text-ink-500 tabular-nums">
          {delta} {deltaLabel}
        </p>
      ) : null}
    </Panel>
  );
}

/** A label/value row. `value === null` renders the caller's absent text. */
export function Readout({
  label,
  value,
  absent,
  unit,
}: {
  label: string;
  value: string | null;
  absent: string;
  unit?: string;
}): React.JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <span className="text-micro text-ink-500">{label}</span>
      {value === null ? (
        <span className="text-micro text-ink-500">{absent}</span>
      ) : (
        <span className="text-label tabular-nums text-ink-100">
          {value}
          {unit ? <span className="text-ink-500">{unit}</span> : null}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Controls
// ---------------------------------------------------------------------------

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: 'primary' | 'quiet';
}

export function Button({
  tone = 'primary',
  className = '',
  ...rest
}: ButtonProps): React.JSX.Element {
  const toneClass =
    tone === 'primary'
      ? 'bg-brand text-canvas hover:bg-brand/90'
      : 'border border-hairline text-ink-300 hover:bg-panel-raised hover:text-ink-100';
  return (
    <button
      className={`rounded px-4 py-2 text-label font-medium transition-colors duration-state focus:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:opacity-50 ${toneClass} ${className}`}
      {...rest}
    />
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>): React.JSX.Element {
  return (
    <input
      className="w-full rounded border border-hairline bg-canvas px-3 py-2 text-body text-ink-100 placeholder:text-ink-500 focus:border-brand focus:outline-none"
      {...props}
    />
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-label text-ink-300">{label}</span>
      {children}
      {hint ? <span className="mt-1 block text-micro text-ink-500">{hint}</span> : null}
    </label>
  );
}

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

/**
 * §15.12 — "skeleton loaders, never spinners" and "no layout shift as data
 * arrives (reserve the space)". A skeleton occupies the height its content
 * will, so a grid does not jump when 35 tiles land.
 */
export function Skeleton({ className = '' }: { className?: string }): React.JSX.Element {
  /* A moving shimmer rather than a static block: a still grey rectangle is
     indistinguishable from a component that rendered empty, and the difference
     between "loading" and "broken" is the whole job of a skeleton. */
  return (
    <div
      className={`animate-shimmer rounded bg-panel-raised bg-gradient-to-r from-transparent via-hairline to-transparent bg-[length:200%_100%] ${className}`}
      aria-hidden="true"
    />
  );
}

export function Notice({
  tone,
  children,
}: {
  tone: 'warn' | 'crit' | 'quiet';
  children: ReactNode;
}): React.JSX.Element {
  const toneClass =
    tone === 'crit'
      ? 'border-crit/40 bg-crit/10 text-crit'
      : tone === 'warn'
        ? 'border-warn/40 bg-warn/10 text-warn'
        : 'border-hairline bg-panel text-ink-300';
  return (
    <div className={`rounded border px-4 py-3 text-label ${toneClass}`} role="status">
      {children}
    </div>
  );
}

export function EmptyState({ message }: { message: string }): React.JSX.Element {
  return (
    <div className="rounded border border-dashed border-hairline px-6 py-10 text-center text-label text-ink-500">
      {message}
    </div>
  );
}
